"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const LOW_LINE = "̲";
const DOUBLE_LOW_LINE = "̳";

function applyCombiningMark(text: string, mark: string): string {
  return [...text].map((char) => `${char}${mark}`).join("");
}

function ResultCard({
  copied,
  label,
  onCopy,
  value,
  copyLabel,
  copiedLabel,
  placeholder,
}: {
  copied: boolean;
  label: string;
  onCopy: () => void;
  value: string;
  copyLabel: string;
  copiedLabel: string;
  placeholder: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={value.length === 0}
          onClick={onCopy}
          type="button"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <p className="min-h-[2rem] whitespace-pre-wrap break-words text-base text-foreground leading-relaxed">
        {value.length > 0 ? value : placeholder}
      </p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const UNDERLINE_TEXT_GENERATOR_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Type or paste text to underline",
  underlineSingle: "Underline (single)",
  doubleUnderline: "Double underline",
  resultPlaceholder: "Your result will appear here",
  copy: "Copy",
  copied: "Copied!",
} as const;

export default function UnderlineTextGeneratorWidget() {
  const strings = useToolUi(UNDERLINE_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [copiedCard, setCopiedCard] = useState<string | null>(null);

  const singleUnderline = applyCombiningMark(input, LOW_LINE);
  const doubleUnderline = applyCombiningMark(input, DOUBLE_LOW_LINE);

  function handleCopy(card: string, value: string) {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCard(card);
      setTimeout(() => setCopiedCard(null), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="underline-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="underline-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Results */}
      <section className="space-y-4">
        <ResultCard
          copied={copiedCard === "single"}
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          label={strings.underlineSingle}
          onCopy={() => handleCopy("single", singleUnderline)}
          placeholder={strings.resultPlaceholder}
          value={input.length > 0 ? singleUnderline : ""}
        />
        <ResultCard
          copied={copiedCard === "double"}
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          label={strings.doubleUnderline}
          onCopy={() => handleCopy("double", doubleUnderline)}
          placeholder={strings.resultPlaceholder}
          value={input.length > 0 ? doubleUnderline : ""}
        />
      </section>
    </>
  );
}
